import { FC, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Formik } from "formik";
import { t } from "i18next";

import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { ButtonPrimary } from "@/shared/components/atoms/ButtonPrimary";

import {
  TaxtIdValues,
  taxValidationSchema,
} from "@/shared/schemas/settings.schema";

import { selectAccessToken } from "@/app/auth/auth.slice";

// API
import { useUpdateTaxIdMutation } from "@/app/settings/tax-id.api";
import { useSessionQuery } from "@/app/auth/auth.api";
import { useGetBranchQuery } from "@/app/branchs/branchs.api";
import { toasts } from "@/shared/utils/toasts";
import { Page } from "@/shared/components/atoms/Page";

export const ImpuestosRecibos: FC<any> = () => {
  const { id } = useParams();
  const accessToken = useSelector(selectAccessToken);
  const [updateTaxId, updateTaxIdResult] = useUpdateTaxIdMutation();

  const getBranchQuery = useGetBranchQuery({
    branchId: id,
  });

  const branch = useMemo(() => getBranchQuery.data, [getBranchQuery]);

  const sessionQuery = useSessionQuery({
    accessToken,
  });

  const handleSubmit = useCallback(
    async (values: TaxtIdValues) => {
      try {
        await updateTaxId({
          branchId: branch.id,
          tax_id: values.tax_id,
          iva: values.iva,
          comment_ticket: values.comment_ticket,
        }).unwrap();
        toasts.success(
          t("contexts.tax_id.tax_id_updated_success"),
          t("contexts.tax_id.tax_id_updated_success_description")
        );
        updateTaxIdResult.reset();
      } catch (error) {
        toasts.danger(
          t("contexts.tax_id.tax_id_updated_failure"),
          t("contexts.tax_id.tax_id_updated_failure_description")
        );
        updateTaxIdResult.reset();
      } finally {
        sessionQuery.refetch();
      }
    },
    [branch]
  );

  const initialValues = useMemo<TaxtIdValues>(() => {
    return {
      tax_id: branch?.tax_id || "",
      iva: branch?.iva || 0,
      comment_ticket: branch?.comment_ticket || "",
    };
  }, [branch]);

  return (
    <Page
      title={t("contexts.tax_id.title")}
      description={t("contexts.tax_id.subtitle")}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={taxValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ handleChange, handleSubmit, errors, values }) => (
          <form
            onSubmit={handleSubmit}
            className="w-full flex flex-col items-center justify-center mt-4"
          >
            <div className="w-full flex flex-col md:flex-row items-start justify-center md:space-x-20 gap-4">
              <InputWithCornerHint
                label={t("share.views.Tax_ID")}
                name="tax_id"
                type="text"
                placeholder={t("share.views.Tax_ID")}
                className="w-full"
                onChange={handleChange}
                error={!!errors.tax_id}
                textError={errors.tax_id}
                value={values.tax_id}
              />
              <InputWithCornerHint
                label={`${t("share.views.Impuesto")} (%)`}
                name="iva"
                type="number"
                placeholder={`${t("share.views.Impuesto")} (%)`}
                onChange={handleChange}
                className="w-full"
                error={!!errors.iva}
                textError={errors.iva}
                value={values.iva}
              />
              <InputWithCornerHint
                label={t("share.views.Comentarios_adicionales")}
                name="comment_ticket"
                type="text"
                placeholder={t("share.views.Comentarios_adicionales")}
                onChange={handleChange}
                className="w-full"
                error={!!errors.comment_ticket}
                textError={errors.comment_ticket}
                value={values.comment_ticket}
              />
            </div>

            <div className="mt-5 flex flex-col md:flex-row justify-center md:w-full gap-4">
              <button
                type="submit"
                className="flex items-center gap-2 justify-center bg-primary text-white px-4 py-2 rounded-md"
              >
                {updateTaxIdResult.isLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                )}
                Guardar
              </button>
            </div>
          </form>
        )}
      </Formik>
    </Page>
  );
};
